import type { Brand } from "./lib/brand";
import type { PractitionerId } from "./practitioner.model";
// ============================================================
// Change log — the aggregate accumulates, the session drains
//
// The order set's delta log and the exam's amendment log are ONE MECHANISM.
// Each command appends to an in-memory list; at commit the session drains it
// and writes one record per entry in the same transaction bundle.
//
// WHY THE ENTITY AND NOT THE HANDLER. The entity is the only thing that knows
// what actually changed — `setParameterValue` to the value it already held is
// a no-op that should produce no entry, and only the entity can tell. The
// rejected alternative, a handler diffing before and after, reconstructs
// intent from a state delta: `moveOrder` and `revokeOrder` + `addOrder` can
// produce the same diff. The log records WHAT THE CLINICIAN DID, not what
// changed.
//
// WHY THE SESSION AND NOT THE HANDLER: ATOMICITY. A log entry written outside
// the bundle can succeed while the state change fails, and then the log is
// lying. The session already owns "one transaction bundle per commit", so it
// is the only place that can guarantee both land together. It also removes a
// real footgun — an entity carrying transient records a forgetful handler
// never drains. The session COLLECTS AND WRITES; it never interprets.
//
// THE HARD CONSTRAINT: THE LOG IS PROVENANCE, NEVER STATE. Nothing reads it to
// decide anything, so the log and the aggregate cannot disagree in the
// direction that hurts. Making the log authoritative instead means
// event-sourcing the aggregate, a far larger commitment than anything else in
// this model. That constraint is structural here, not a convention: the buffer
// is a `#private` field, so not even a subclass can read back what it appended
// — the only way out is the drain, and the drain belongs to the session.
// ============================================================

/**
 * One recorded change.
 *
 * ENTRIES ARE SELF-SUFFICIENT: each change carries its own `from` and `to`
 * VALUES, not references, so it still reads correctly after a parameter is
 * renamed or a definition revised. `kind` is the only field this can require;
 * the rest is per-aggregate, and the rule that the payload must be plain
 * copied data rather than a pointer at live state is what makes the entry
 * readable a year later.
 */
export interface SelfSufficientChange {
  readonly kind: string;
}

/**
 * A group of changes recorded as one act.
 *
 * `reason` sits HERE and not on each command, because one clinician action
 * spans several commands and the reason explains the intent behind the set —
 * a swap is one act by one person, not two unexplained moves. Optional;
 * requiring it yields "n/a" on every row.
 *
 * Cost, accepted: an individual change is not self-sufficient — it needs its
 * parent for who and when. That is fine ONLY because changes are never read
 * detached from their entry, which is a rule, not an assumption.
 */
export interface ChangeLogEntry<Change extends SelfSufficientChange> {
  /** Absent until the session writes it. The store assigns the identity. */
  readonly id?: ChangeLogId;
  readonly at: string;
  /** Practitioner id. Who did it. */
  readonly by: PractitionerId;
  readonly reason?: string;
  readonly changes: readonly Change[];
}

/** Who and when, supplied at the drain because the commit is what the entry describes. */
interface DrainContext {
  at: string;
  by: PractitionerId;
  reason?: string;
}

/**
 * The one thing `engine` must learn about domain entities beyond mapping:
 * an aggregate may emit records. The session calls this on every tracked
 * aggregate at commit and adds whatever comes back to the bundle.
 */
interface EmitsChangeLog<Change extends SelfSufficientChange = SelfSufficientChange> {
  hasPendingChanges(): boolean;
  drainChangeLog(context: DrainContext): readonly ChangeLogEntry<Change>[];
}

/**
 * The accumulator every logging aggregate extends.
 *
 * One entry per drain, which is one entry per commit — the commit boundary IS
 * the act boundary, and that is exactly the grouping the doc's swap example
 * shows: two moves, one entry, one reason.
 */
export abstract class ChangeLogAccumulator<Change extends SelfSufficientChange> implements EmitsChangeLog<Change> {
  // Private, not protected, and deliberately unreadable from a subclass: the
  // log is provenance and nothing may branch on it. Making it `#` means a
  // future command CANNOT consult what it already recorded even by accident.
  #pending: Change[] = [];

  /**
   * Record what a command actually did. Commands call this only when
   * something moved — a no-op write produces no entry, because a log full of
   * "set the value it already had" is noise a reviewer has to filter.
   */
  protected recordChange(change: Change): void {
    this.#pending.push(change);
  }

  hasPendingChanges(): boolean {
    return this.#pending.length > 0;
  }

  /**
   * Hand the accumulated changes to the session and forget them. Returns an
   * empty list when nothing moved, so the session writes no record for a
   * commit that changed nothing.
   */
  drainChangeLog(context: DrainContext): readonly ChangeLogEntry<Change>[] {
    if (this.#pending.length === 0) return [];
    const changes = this.#pending;
    this.#pending = [];
    return [{ at: context.at, by: context.by, reason: context.reason, changes }];
  }
}

/** Store-assigned change-log entry identity. */
export type ChangeLogId = Brand<string, "ChangeLogId">;
export function asChangeLogId(value: string): ChangeLogId {
  return value as ChangeLogId;
}
